import {timer, from, type Subscription} from 'rxjs';
import {flatMap} from 'rxjs/operators';

import ENV from 'web-app/config/environment';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {generateHash} from '@famly/vet_navigation_route-config';

const POLLING_INTERVAL = 2 * 60 * 1000; // 2 minutes
const TIMEOUT = 5 * 1000; // 5 seconds
const KILL_SIGNAL_TRIGGER = 'KILL_792D44F7-131B-4ACA-A756-A673AD2B34D1';

/*
    Determines whether the app should be "killed" by fetching a remote resource.
    This way, we can remotely stop our users from using the app.

    Apparently finally is not part of the fetch spec since it is not part of the promise spec.
    Reflecting that in the return type.
 */
export const shouldKill = (): Omit<Promise<boolean>, 'finally'> => {
    // Not all environments (e.g. development) have a killswitch url
    if (!hasValue(ENV.killswitchUrl)) {
        return Promise.resolve(false);
    }

    // Using an `AbortController` as `fetch` doesn't have custom timeout built in.
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(new Error('Aborted kill signal check')), TIMEOUT);

    // We only give the kill signal if we can confirm that the remote resource contains the trigger.
    // Ohterwise, we resume all is well.
    return fetch(ENV.killswitchUrl, {signal: controller.signal})
        .then(response => {
            return response.text();
        })
        .then(result => {
            clearTimeout(id);
            return result.trim() === KILL_SIGNAL_TRIGGER;
        })
        .catch(() => {
            // To avoid flooding Sentry, we've actively chosen NOT to post errors here.
            clearTimeout(id);
            return false;
        });
};

let killswitchSubscription: Subscription | null = null;

export const stopListeningForKillswitch = () => {
    killswitchSubscription?.unsubscribe();
    killswitchSubscription = null;
};

// Poll once in a while for the file that controls the kill switch.
// If the file contains the magic phrase, then redirect to an error page.
export const startListeningForKillswitch = () => {
    // We shouldn't be able to start multiple subscriptions.
    // One listener for the killswitch is enough.
    if (hasValue(killswitchSubscription)) {
        return;
    }

    const observable = timer(0, POLLING_INTERVAL).pipe(
        flatMap(() => {
            return from(shouldKill());
        }),
    );

    killswitchSubscription = observable.subscribe(shouldKill => {
        if (shouldKill) {
            window.location.replace(generateHash('/unavailable'));
        }
    });
};
